import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { ttsApi } from '../services/api';
import AudioUploader, { UploadedAudio } from '../components/AudioUploader';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const PromptTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: theme.palette.divider },
    '&:hover fieldset': { borderColor: theme.palette.primary.main },
    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
  },
}));

function makeAudioDataUri(dataUriOrBase64: string, contentType?: string): string {
  if (dataUriOrBase64.startsWith('http://') || dataUriOrBase64.startsWith('https://')) {
    return dataUriOrBase64;
  }
  if (dataUriOrBase64.startsWith('data:')) return dataUriOrBase64;
  const ct = contentType || 'audio/flac';
  return `data:${ct};base64,${dataUriOrBase64}`;
}

const TextToSpeechGenerator: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [uploadedVoice, setUploadedVoice] = useState<UploadedAudio | null>(null);
  const [voiceMode, setVoiceMode] = useState<'predefined' | 'upload'>('predefined');
  const [voices, setVoices] = useState<Record<string, string>>({});
  const [selectedPredefined, setSelectedPredefined] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [taskId, setTaskId] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartTimeRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, []);

  // Fetch predefined voices
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch('/api/v1/tts/voices');
        const data: any = await resp.json();
        const list = ((data && data.voices) ? data.voices : {}) as Record<string, string>;
        setVoices(list);
        // Pick the first voice by default
        const first = Object.keys(list)[0] || '';
        setSelectedPredefined(first);
      } catch (e) {
        console.error('Failed to fetch predefined voices', e);
      }
    })();
  }, []);

  const onUpload = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const preview = String(reader.result);
      setUploadedVoice({ file, preview });
      setAudioUrl(preview); // Show uploaded audio in the main player
    };
    reader.readAsDataURL(file);
  };
  const onRemove = () => {
    setUploadedVoice(null);
    setAudioUrl(null); // Clear the main player when removing uploaded audio
  };

  const clearPolling = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const startProgressPolling = (id: string) => {
    const pollIntervalMs = 4000;
    const maxPollTime = 5 * 60 * 1000;
    setGenerationProgress(0);
    setStatusMessage('Starting text-to-speech generation...');
    pollStartTimeRef.current = Date.now();
    clearPolling();

    let currentIntervalId: any = null;
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 5;

    const pollForStatus = async () => {
      const currentTime = Date.now();
      const startTime = pollStartTimeRef.current ?? currentTime;
      const elapsed = currentTime - startTime;
      if (elapsed > maxPollTime) {
        if (currentIntervalId) {
          clearInterval(currentIntervalId);
          progressIntervalRef.current = null;
        }
        setErrorMessage('Task timeout - generation took too long');
        setIsGenerating(false);
        return;
      }
      try {
        const resp = await ttsApi.checkStatus(id);
        const { status, progress, statusMessages } = resp as any;
        if (typeof progress === 'number') setGenerationProgress(progress * 100);
        if (statusMessages) setStatusMessage(statusMessages.detail_message || statusMessages.message || '');
        else if (typeof progress === 'number') setStatusMessage(`Processing: ${Math.round(progress * 100)}%`);

        if (status && status.toLowerCase() === 'in_queue') {
          setStatusMessage('Queued. Waiting to start...');
          return;
        }
        if (status && status.toLowerCase() === 'cancelled') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setStatusMessage('TTS generation cancelled');
          setErrorMessage('Task was cancelled');
          setIsGenerating(false);
          return;
        }
        if (status && status.toLowerCase() === 'failed') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          const failureReason = statusMessages?.message || statusMessages?.detail_message || 'Unknown error';
          setStatusMessage(`TTS generation failed: ${failureReason}`);
          setErrorMessage(`Task failed: ${failureReason}`);
          setIsGenerating(false);
          return;
        }
        if (status && status.toLowerCase() === 'completed') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setStatusMessage('TTS completed. Fetching result ...');
          const { audio } = await ttsApi.getResult(id);
          const data_uri = audio?.data_uri;
          const ct = audio?.content_type;
          if (audio && data_uri) {
            setStatusMessage('TTS generation completed.');
            const url = makeAudioDataUri(String(data_uri), ct);
            setAudioUrl(url);
            setIsGenerating(false);
          } else {
            setErrorMessage('TTS generated but no audio was provided');
            setIsGenerating(false);
          }
        }
      } catch (err: any) {
        consecutiveErrors++;
        if (consecutiveErrors >= maxConsecutiveErrors) {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setErrorMessage(`Too many polling errors: ${err.message || 'Unknown error'}`);
          setIsGenerating(false);
        } else {
          setStatusMessage(`Polling error: ${err.message || 'Unknown error'} - Will retry... (${consecutiveErrors}/${maxConsecutiveErrors})`);
        }
      }
    };

    pollForStatus();
    const interval = setInterval(pollForStatus, pollIntervalMs);
    currentIntervalId = interval;
    progressIntervalRef.current = interval;
    return interval;
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      alert('Please enter a prompt');
      return;
    }
    if (voiceMode === 'predefined' && !selectedPredefined) {
      alert('Please select a predefined voice or switch to upload your own.');
      return;
    }
    setErrorMessage('');
    setStatusMessage('Submitting request...');
    setIsGenerating(true);
    setAudioUrl(null);
    try {
      const voiceFile = voiceMode === 'upload' ? uploadedVoice?.file : undefined;
      const predefinedVoice = voiceMode === 'predefined' ? selectedPredefined : undefined;
      const { request_id } = await ttsApi.generateSpeech(prompt, voiceFile, predefinedVoice);
      setTaskId(request_id);
      startProgressPolling(request_id);
    } catch (error: any) {
      const errorDetail = error.response?.data?.detail || error.message || 'Unknown error';
      setErrorMessage(`Failed to start TTS: ${errorDetail}`);
      alert(`Error: ${errorDetail}`);
      setIsGenerating(false);
    }
  };

  const handleCancel = async () => {
    if (taskId) {
      try {
        clearPolling();
        await ttsApi.cancelTask(taskId);
        setStatusMessage('TTS generation cancelled');
      } catch (err: any) {
        setErrorMessage('Error cancelling task: ' + (err.message || 'Unknown error'));
      } finally {
        setIsGenerating(false);
      }
    }
  };

  const handleNew = () => {
    clearPolling();
    setPrompt('');
    setUploadedVoice(null);
    setAudioUrl(null);
    setTaskId(null);
    setGenerationProgress(0);
    setStatusMessage('');
    setErrorMessage('');
  };

  return (
    <Grid container spacing={3} sx={{ height: 'calc(100vh - 120px)' }}>
      <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <StyledPaper elevation={3} sx={{ mb: 3, flex: '1 1 auto' }}>
          <Typography variant="h5" gutterBottom>
            Voice Options
          </Typography>
          <RadioGroup
            row
            value={voiceMode}
            onChange={(e) => {
              const mode = (e.target as HTMLInputElement).value as 'predefined' | 'upload';
              setVoiceMode(mode);
              // Update audio URL when switching modes
              if (mode === 'predefined' && uploadedVoice) {
                setAudioUrl(null); // Clear when switching to predefined
              } else if (mode === 'upload' && uploadedVoice) {
                setAudioUrl(uploadedVoice.preview); // Show uploaded audio when switching to upload
              }
            }}
          >
            <FormControlLabel value="predefined" control={<Radio />} label="Use Predefined Voice" />
            <FormControlLabel value="upload" control={<Radio />} label="Upload My Own" />
          </RadioGroup>

          {voiceMode === 'predefined' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <FormControl sx={{ minWidth: 240 }}>
                <InputLabel id="predefined-voice-label">Predefined Voice</InputLabel>
                <Select
                  labelId="predefined-voice-label"
                  id="predefined-voice"
                  value={selectedPredefined}
                  label="Predefined Voice"
                  disabled={isGenerating}
                  onChange={(e) => setSelectedPredefined(e.target.value as string)}
                >
                  {Object.keys(voices).map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedPredefined && voices[selectedPredefined] && (
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Sample preview for "{selectedPredefined}"
                  </Typography>
                  <audio src={voices[selectedPredefined]} controls style={{ width: '100%' }} />
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ mb: 3, flex: '0 0 auto' }}>
              <AudioUploader uploadedAudio={uploadedVoice} onUpload={onUpload} onRemove={onRemove} />
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="h5" gutterBottom>
            Prompt
          </Typography>
          <PromptTextField
            fullWidth
            multiline
            rows={8}
            variant="outlined"
            placeholder="What should the voice say?"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 'auto' }}>
            {!audioUrl ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={!prompt.trim() || isGenerating}
                  onClick={handleGenerate}
                  startIcon={<GraphicEqIcon />}
                  fullWidth
                >
                  Generate Speech
                </Button>
                {isGenerating && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="large"
                    onClick={handleCancel}
                    startIcon={<StopCircleIcon />}
                  >
                    Cancel
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => {
                  // Do not clear existing inputs; just start a new generation with current prompt/voice
                  clearPolling();
                  setAudioUrl(null);
                  setStatusMessage('');
                  setErrorMessage('');
                  setGenerationProgress(0);
                  handleGenerate();
                }}
                startIcon={<RefreshIcon />}
                fullWidth
              >
                New Generation
              </Button>
            )}
          </Box>
        </StyledPaper>
      </Grid>

      <Grid item xs={12} md={6} sx={{ height: '100%' }}>
        <StyledPaper elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h5" gutterBottom>
            {voiceMode === 'upload' && uploadedVoice && !isGenerating ? 'Uploaded Voice' : 'Generated Audio'}
          </Typography>
          {isGenerating ? (
            <Box sx={{ width: '100%', mt: 4, position: 'relative' }}>
              <Typography variant="body1" gutterBottom>
                {statusMessage || 'Generating audio...'}
              </Typography>
              {errorMessage ? (
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    borderLeft: '4px solid',
                    borderColor: 'error.main',
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Error Occurred
                  </Typography>
                  <Typography variant="body1">{errorMessage}</Typography>
                </Paper>
              ) : (
                <>
                  <LinearProgress
                    variant="determinate"
                    value={generationProgress}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      mb: 1,
                      '& .MuiLinearProgress-bar': { animation: 'pulse 1.5s infinite ease-in-out' },
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" align="right">{`${Math.round(
                    generationProgress,
                  )}%`}</Typography>
                </>
              )}
              <Box
                sx={{
                  mt: 3,
                  width: '100%',
                  height: '200px',
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  position: 'relative',
                }}
              >
                <Typography variant="h6" color="white" align="center">
                  {statusMessage || 'Processing your audio...'}
                </Typography>
              </Box>
            </Box>
          ) : audioUrl ? (
            <Box sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: '0 0 auto' }}>
                <audio src={audioUrl} controls style={{ width: '100%' }} />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <Typography variant="body1">No audio generated yet. Enter a prompt to get started.</Typography>
            </Box>
          )}
        </StyledPaper>
      </Grid>
    </Grid>
  );
};

export default TextToSpeechGenerator;
